import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import {
  ClipboardText, FilePdf, FloppyDisk, Plus, Trash, UploadSimple, Camera,
  CheckSquare, Square, PencilSimple, Eraser, Check,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem('erp_token')
    || localStorage.getItem('access_token')
    || localStorage.getItem('token');
}

// =============== Signature Canvas (inline) ===============
function SignatureCanvas({ value, onChange }) {
  const canvasRef = React.useRef(null);
  const drawing = React.useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (value && value.startsWith('data:image')) {
      const img = new Image();
      img.onload = () => { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height); };
      img.src = value;
    }
  }, [value]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - r.left) / r.width) * c.width, y: ((t.clientY - r.top) / r.height) * c.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const p = pos(e); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const draw = (e) => { if (!drawing.current) return; e.preventDefault(); const p = pos(e); const ctx = canvasRef.current.getContext('2d'); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);
    onChange('');
  };

  return (
    <div className="border border-border rounded-md p-2 bg-white">
      <canvas
        ref={canvasRef}
        width={500}
        height={140}
        className="border border-dashed border-gray-300 rounded touch-none w-full"
        style={{ touchAction: 'none' }}
        onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
        data-testid="sr-sig-canvas"
      />
      <div className="flex justify-between items-center text-xs mt-1">
        <span className="text-muted-foreground">Sign with mouse or finger</span>
        <Button type="button" size="sm" variant="ghost" onClick={clear} className="h-6 text-red-600" data-testid="sr-sig-clear">
          <Eraser size={12} className="mr-1" /> Clear
        </Button>
      </div>
    </div>
  );
}

// =============== Main Dialog ===============
export default function ServiceReportDialog({ open, onOpenChange, srId }) {
  const [sr, setSr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [activeTab, setActiveTab] = useState('checklist');

  const load = useCallback(async () => {
    if (!srId || !open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/service-reports/${srId}`);
      setSr(res.data);
    } catch (e) { toast.error('Could not load Service Report'); }
    finally { setLoading(false); }
  }, [srId, open]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setSr(s => ({ ...s, [k]: v }));

  const save = async (alsoDownload = false) => {
    if (!sr) return;
    setWorking(true);
    try {
      const body = {
        service_date: sr.service_date,
        work_done: sr.work_done,
        comments: sr.comments,
        next_visit_required: !!sr.next_visit_required,
        next_visit_date: sr.next_visit_date,
        next_visit_reason: sr.next_visit_reason,
        customer_rep_name: sr.customer_rep_name,
        customer_rep_signature: sr.customer_rep_signature,
        attended_by: sr.attended_by || [],
        materials_used: sr.materials_used || [],
        checklist: sr.checklist || [],
        status: sr.status,
      };
      const res = await axios.patch(`${API}/service-reports/${srId}`, body);
      setSr(res.data);
      toast.success('Service Report saved');
      if (alsoDownload) downloadPdf();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setWorking(false); }
  };

  const downloadPdf = () => {
    if (!srId) return;
    fetch(`${API}/service-reports/${srId}/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `service-report-${sr?.sr_number || srId}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('PDF failed'));
  };

  const uploadFile = async (kind, file) => {
    const fd = new FormData(); fd.append('file', file); fd.append('kind', kind);
    try {
      await axios.post(`${API}/service-reports/${srId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${kind}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };

  // Checklist editing
  const toggleCheck = (i) => setSr(s => ({ ...s, checklist: s.checklist.map((c, idx) => idx === i ? { ...c, checked: !c.checked } : c) }));
  const updateCheck = (i, key, val) => setSr(s => ({ ...s, checklist: s.checklist.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));
  const addCheckRow = () => setSr(s => ({ ...s, checklist: [...(s.checklist || []), { id: crypto.randomUUID?.() || String(Math.random()), section: 'General', description: '', checked: false, comment: '' }] }));
  const removeCheckRow = (i) => setSr(s => ({ ...s, checklist: s.checklist.filter((_, idx) => idx !== i) }));

  // Materials
  const addMaterial = () => setSr(s => ({ ...s, materials_used: [...(s.materials_used || []), { id: crypto.randomUUID?.() || String(Math.random()), name: '', quantity: 1, unit: 'Nos', notes: '' }] }));
  const updateMaterial = (i, key, val) => setSr(s => ({ ...s, materials_used: s.materials_used.map((m, idx) => idx === i ? { ...m, [key]: val } : m) }));
  const removeMaterial = (i) => setSr(s => ({ ...s, materials_used: s.materials_used.filter((_, idx) => idx !== i) }));

  // Attended by
  const addAttendant = () => setSr(s => ({ ...s, attended_by: [...(s.attended_by || []), { id: crypto.randomUUID?.() || String(Math.random()), name: '', role: '' }] }));
  const updateAttendant = (i, key, val) => setSr(s => ({ ...s, attended_by: s.attended_by.map((a, idx) => idx === i ? { ...a, [key]: val } : a) }));
  const removeAttendant = (i) => setSr(s => ({ ...s, attended_by: s.attended_by.filter((_, idx) => idx !== i) }));

  if (!srId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="service-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardText size={20} weight="duotone" />
            {sr ? `Service Report ${sr.sr_number}` : 'Service Report'}
            {sr?.job_number && <Badge variant="outline" className="text-xs">JO: {sr.job_number}</Badge>}
            {sr?.status && <Badge variant="outline" className="text-xs capitalize">{sr.status}</Badge>}
          </DialogTitle>
          <DialogDescription>
            Site team fills this in after attending. The customer representative signs at the bottom. Branded PDF can be downloaded for handover.
          </DialogDescription>
        </DialogHeader>

        {loading || !sr ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (
          <>
            <div className="flex gap-1 border-b border-border mb-2 flex-shrink-0 overflow-x-auto">
              {[
                { v: 'checklist', label: `Checklist (${(sr.checklist || []).length})` },
                { v: 'work', label: 'Work Done' },
                { v: 'materials', label: `Materials (${(sr.materials_used || []).length})` },
                { v: 'attendants', label: `Attendants (${(sr.attended_by || []).length})` },
                { v: 'photos', label: 'Photos' },
                { v: 'signoff', label: 'Sign-off & Next Visit' },
              ].map(t => (
                <button key={t.v} onClick={() => setActiveTab(t.v)}
                  className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition ${activeTab === t.v ? 'border-violet-600 text-violet-700 dark:text-violet-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  data-testid={`sr-tab-${t.v}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <ScrollArea className="flex-1 pr-3">
              {/* CHECKLIST */}
              {activeTab === 'checklist' && (
                <div className="space-y-3 py-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Site team ticks completed items. Carried over from the Job Order checklist.</p>
                    <Button size="sm" variant="outline" onClick={addCheckRow}><Plus size={12} className="mr-1" /> Add</Button>
                  </div>
                  {(sr.checklist || []).length === 0 ? (
                    <div className="border border-dashed rounded-md p-6 text-center text-xs text-muted-foreground">No checklist items.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded-md">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left px-2 py-1 w-28">Section</th>
                          <th className="text-center w-10">✓</th>
                          <th className="text-left px-2 py-1">Item</th>
                          <th className="text-left px-2 py-1 w-44">Comment</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {sr.checklist.map((c, i) => (
                          <tr key={c.id || i} className={c.checked ? 'bg-emerald-50/30' : ''}>
                            <td className="px-2 py-1"><Input value={c.section || ''} onChange={e => updateCheck(i, 'section', e.target.value)} className="h-7 text-xs" /></td>
                            <td className="text-center">
                              <button type="button" onClick={() => toggleCheck(i)} data-testid={`sr-check-${i}`}>
                                {c.checked ? <CheckSquare size={20} weight="fill" className="text-emerald-600" /> : <Square size={20} />}
                              </button>
                            </td>
                            <td className="px-2 py-1"><Input value={c.description || ''} onChange={e => updateCheck(i, 'description', e.target.value)} className="h-7 text-xs" /></td>
                            <td className="px-2 py-1"><Input value={c.comment || ''} onChange={e => updateCheck(i, 'comment', e.target.value)} className="h-7 text-xs" placeholder="Site notes…" /></td>
                            <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeCheckRow(i)}><Trash size={12} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* WORK DONE */}
              {activeTab === 'work' && (
                <div className="space-y-3 py-2">
                  <div>
                    <Label>Service date</Label>
                    <Input type="date" value={sr.service_date || ''} onChange={e => setField('service_date', e.target.value)} className="w-48" data-testid="sr-date" />
                  </div>
                  <div>
                    <Label>Work done <span className="text-red-500">*</span></Label>
                    <Textarea rows={6} value={sr.work_done || ''} onChange={e => setField('work_done', e.target.value)}
                              placeholder="Describe in detail what was carried out on site…" data-testid="sr-work-done" />
                  </div>
                  <div>
                    <Label>Comments / observations</Label>
                    <Textarea rows={4} value={sr.comments || ''} onChange={e => setField('comments', e.target.value)}
                              placeholder="Anything the customer should know — issues observed, recommendations…" data-testid="sr-comments" />
                  </div>
                </div>
              )}

              {/* MATERIALS */}
              {activeTab === 'materials' && (
                <div className="space-y-3 py-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Spare parts &amp; consumables used during the service.</p>
                    <Button size="sm" variant="outline" onClick={addMaterial} data-testid="sr-add-material"><Plus size={12} className="mr-1" /> Add material</Button>
                  </div>
                  {(sr.materials_used || []).length === 0 ? (
                    <div className="border border-dashed rounded-md p-6 text-center text-xs text-muted-foreground">No materials used.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded-md">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left px-2 py-1">Material / part</th>
                          <th className="text-center px-2 py-1 w-20">Qty</th>
                          <th className="text-left px-2 py-1 w-24">Unit</th>
                          <th className="text-left px-2 py-1">Notes</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {sr.materials_used.map((m, i) => (
                          <tr key={m.id || i}>
                            <td className="px-2 py-1"><Input value={m.name || ''} onChange={e => updateMaterial(i, 'name', e.target.value)} className="h-7 text-xs" data-testid={`sr-mat-name-${i}`} /></td>
                            <td className="px-2 py-1"><Input type="number" value={m.quantity || 0} onChange={e => updateMaterial(i, 'quantity', parseFloat(e.target.value) || 0)} className="h-7 text-xs text-center" /></td>
                            <td className="px-2 py-1"><Input value={m.unit || ''} onChange={e => updateMaterial(i, 'unit', e.target.value)} className="h-7 text-xs" placeholder="Nos / Mtr / Kg" /></td>
                            <td className="px-2 py-1"><Input value={m.notes || ''} onChange={e => updateMaterial(i, 'notes', e.target.value)} className="h-7 text-xs" /></td>
                            <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeMaterial(i)}><Trash size={12} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ATTENDANTS */}
              {activeTab === 'attendants' && (
                <div className="space-y-3 py-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Technicians, helpers, supervisors who attended the job.</p>
                    <Button size="sm" variant="outline" onClick={addAttendant} data-testid="sr-add-attendant"><Plus size={12} className="mr-1" /> Add person</Button>
                  </div>
                  {(sr.attended_by || []).length === 0 ? (
                    <div className="border border-dashed rounded-md p-6 text-center text-xs text-muted-foreground">No attendants recorded.</div>
                  ) : (
                    <table className="w-full text-xs border border-border rounded-md">
                      <thead className="bg-muted/30">
                        <tr>
                          <th className="text-left px-2 py-1">Name</th>
                          <th className="text-left px-2 py-1 w-44">Role</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {sr.attended_by.map((a, i) => (
                          <tr key={a.id || i}>
                            <td className="px-2 py-1"><Input value={a.name || ''} onChange={e => updateAttendant(i, 'name', e.target.value)} className="h-7 text-xs" data-testid={`sr-att-name-${i}`} /></td>
                            <td className="px-2 py-1"><Input value={a.role || ''} onChange={e => updateAttendant(i, 'role', e.target.value)} className="h-7 text-xs" placeholder="Lead Technician / Helper / Supervisor" /></td>
                            <td><Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => removeAttendant(i)}><Trash size={12} /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* PHOTOS */}
              {activeTab === 'photos' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 py-2">
                  {['before', 'after', 'attachment'].map(kind => {
                    const list = kind === 'before' ? (sr.before_pictures || [])
                              : kind === 'after' ? (sr.after_pictures || [])
                              : (sr.attachments || []);
                    return (
                      <div key={kind} className="border rounded-md p-3">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="flex items-center gap-1 m-0 capitalize text-xs">
                            <Camera size={12} /> {kind === 'attachment' ? 'Attachments' : `${kind} pictures`} ({list.length})
                          </Label>
                          <label className="cursor-pointer">
                            <input type="file" className="hidden"
                                   accept={kind === 'attachment' ? '*' : 'image/*'}
                                   onChange={e => e.target.files?.[0] && uploadFile(kind, e.target.files[0])}
                                   data-testid={`sr-upload-${kind}`} />
                            <Button size="sm" variant="outline" type="button" onClick={(e) => { e.preventDefault(); e.currentTarget.previousElementSibling.click(); }}>
                              <UploadSimple size={12} className="mr-1" /> Upload
                            </Button>
                          </label>
                        </div>
                        <div className="space-y-1 max-h-40 overflow-auto">
                          {list.map(a => (
                            <div key={a.id} className="text-xs flex items-center justify-between">
                              <span className="truncate">📎 {a.filename}</span>
                            </div>
                          ))}
                          {list.length === 0 && <div className="text-xs text-muted-foreground italic">None</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* SIGN-OFF & NEXT VISIT */}
              {activeTab === 'signoff' && (
                <div className="space-y-4 py-2">
                  <div className="border rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10">
                    <Label className="flex items-center gap-2 mb-1">
                      Next Visit
                      <input type="checkbox" checked={!!sr.next_visit_required} onChange={e => setField('next_visit_required', e.target.checked)} data-testid="sr-next-visit-toggle" />
                      <span className="text-xs text-muted-foreground">Tick if a follow-up visit is needed</span>
                    </Label>
                    {sr.next_visit_required && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div>
                          <Label className="text-xs">Date</Label>
                          <Input type="date" value={sr.next_visit_date || ''} onChange={e => setField('next_visit_date', e.target.value)} data-testid="sr-next-visit-date" />
                        </div>
                        <div>
                          <Label className="text-xs">Reason</Label>
                          <Input value={sr.next_visit_reason || ''} onChange={e => setField('next_visit_reason', e.target.value)} placeholder="e.g. Next quarterly PPM" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border rounded-md p-3 bg-blue-50/30 dark:bg-blue-900/10">
                    <Label className="flex items-center gap-1 mb-1 m-0">
                      <PencilSimple size={14} /> Customer Representative Sign-off
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">The customer\'s representative signs here to confirm the work is acceptable.</p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Representative name</Label>
                        <Input value={sr.customer_rep_name || ''} onChange={e => setField('customer_rep_name', e.target.value)} placeholder="Full name" data-testid="sr-rep-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Signature</Label>
                        <SignatureCanvas value={sr.customer_rep_signature || ''} onChange={(v) => setField('customer_rep_signature', v)} />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Status:</Label>
                    <select
                      value={sr.status || 'draft'}
                      onChange={e => setField('status', e.target.value)}
                      className="border border-border rounded h-9 px-2 text-sm bg-background"
                      data-testid="sr-status"
                    >
                      <option value="draft">Draft</option>
                      <option value="submitted">Submitted (sent for review)</option>
                      <option value="approved">Approved (closed)</option>
                    </select>
                  </div>
                </div>
              )}
            </ScrollArea>
          </>
        )}

        <DialogFooter className="border-t pt-3 flex-shrink-0 flex-wrap gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button variant="outline" onClick={downloadPdf} data-testid="sr-pdf-download">
            <FilePdf size={14} className="mr-1" /> Download PDF
          </Button>
          <Button variant="outline" onClick={() => save(true)} disabled={working} data-testid="sr-save-pdf">
            <FilePdf size={14} className="mr-1" /> Save &amp; Download PDF
          </Button>
          <Button onClick={() => save(false)} disabled={working} data-testid="sr-save">
            <FloppyDisk size={14} className="mr-1" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
